import { useState } from 'react';
import { Hash, Plus, X, Pencil } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  useCostCodes,
  useCreateCostCode,
  useUpdateCostCode,
  useDeleteCostCode,
} from '@/hooks/use-payroll';
import type { CostCode, CostCodeCategory } from '@/lib/types';

const CATEGORY_OPTIONS: { value: CostCodeCategory; label: string }[] = [
  { value: 'labor', label: 'Labor' },
  { value: 'materials', label: 'Materials' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'travel', label: 'Travel' },
  { value: 'overhead', label: 'Overhead' },
  { value: 'other', label: 'Other' },
];

const categoryColors: Record<string, string> = {
  labor: 'bg-blue-100 text-blue-800',
  materials: 'bg-green-100 text-green-800',
  equipment: 'bg-purple-100 text-purple-800',
  travel: 'bg-orange-100 text-orange-800',
  overhead: 'bg-gray-100 text-gray-800',
  other: 'bg-yellow-100 text-yellow-800',
};

export function CostCodesPage() {
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data: costCodes, isLoading } = useCostCodes({
    category: filterCategory || undefined,
  });
  const createCostCode = useCreateCostCode();
  const updateCostCode = useUpdateCostCode();
  const deleteCostCode = useDeleteCostCode();

  // Form state
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<CostCodeCategory>('other');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [formError, setFormError] = useState('');

  const resetForm = () => {
    setCode('');
    setName('');
    setCategory('other');
    setDescription('');
    setIsActive(true);
    setFormError('');
    setShowForm(false);
    setEditingId(null);
  };

  const startEdit = (cc: CostCode) => {
    setCode(cc.code);
    setName(cc.name);
    setCategory(cc.category);
    setDescription(cc.description);
    setIsActive(cc.is_active);
    setEditingId(cc.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!code || !name) {
      setFormError('Code and name are required.');
      return;
    }

    try {
      if (editingId) {
        await updateCostCode.mutateAsync({
          id: editingId,
          data: { code, name, category, description, is_active: isActive },
        });
      } else {
        await createCostCode.mutateAsync({
          code,
          name,
          category,
          description,
          is_active: isActive,
        });
      }
      resetForm();
    } catch {
      setFormError(editingId ? 'Failed to update cost code.' : 'Failed to create cost code.');
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Delete this cost code?')) {
      deleteCostCode.mutate(id);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Cost Codes</h1>
          <p className="text-gray-600 mt-1">Chart of Accounts integration for expense tracking</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Cost Code
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Filter by Category</Label>
              <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editingId ? 'Edit Cost Code' : 'New Cost Code'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{formError}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Code</Label>
                <Input placeholder="e.g., 5010" value={code} onChange={(e) => setCode(e.target.value)} />
              </div>
              <div>
                <Label>Name</Label>
                <Input placeholder="e.g., Direct Labor" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={(v) => setCategory(v as CostCodeCategory)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Active
                </label>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input placeholder="Optional description..." value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <Button onClick={handleSubmit} disabled={createCostCode.isPending || updateCostCode.isPending}>
              {(createCostCode.isPending || updateCostCode.isPending) ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cost Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Codes</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading cost codes...</div>
          ) : !costCodes?.length ? (
            <div className="p-8 text-center text-gray-500">
              <Hash className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No cost codes configured yet</p>
              <Button className="mt-4" variant="outline" onClick={() => setShowForm(true)}>
                Add a cost code
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="px-4 pb-2">Code</th>
                    <th className="px-4 pb-2">Name</th>
                    <th className="px-4 pb-2">Category</th>
                    <th className="px-4 pb-2">Status</th>
                    <th className="px-4 pb-2">Description</th>
                    <th className="px-4 pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {costCodes.map((cc) => (
                    <tr key={cc.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono font-medium">{cc.code}</td>
                      <td className="px-4 py-2 font-medium">{cc.name}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[cc.category] || 'bg-gray-100'}`}>
                          {cc.category}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={cc.is_active ? 'text-green-600' : 'text-gray-400'}>
                          {cc.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 truncate max-w-[200px]">{cc.description}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(cc)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(cc.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
